import { groupBy, keys } from "lodash";
import { FunctionComponent, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import AddonModal from "rivals/components/shared/AddonOptions/AddonModal";
import {
  setActiveAddOn,
  setModalStep
} from "rivals/redux/subscriptionAddons/actions";
import {
  getActiveAddOn,
  getFilteredNonPurchasedChooseAddOns,
  getSubscriptionEligibleAddons,
  getUserEligibleAddons
} from "rivals/redux/subscriptionAddons/selectors";
import {
  getPurchasedSpyAddOns,
  selectHasAdLite
} from "rivals/redux/user/selectors";
import { ADD_ON_PRIORITY_ORDER } from "rivals/shared/constants";
import { AddOn } from "rivals/shared/interfaces/AddOn";
import { setData } from "rivals/shared/utils/qa";
import AddonCard from "./AddonCard";
import styles from "./AddonOptions.module.scss";
import { ADDON_MODAL_CONSTANTS } from "./constants";
import strings from "./strings";
import { OwnProps } from "./types";

const { AD_LITE, BUNDLE } = ADDON_MODAL_CONSTANTS;
const { dataPhilter, spyName } = strings;

const AddonOptions: FunctionComponent<OwnProps> = ({
  headerText,
  useAddOnsForSubscription
}): JSX.Element | null => {
  const [showModal, toggleShowModal] = useState(false);
  const [chooseYourOwnAddOn, changeChooseYourOwnAddOn] = useState<
    number | undefined
  >(undefined);
  const dispatch = useDispatch();
  const existingSpyAddOns = useSelector(getPurchasedSpyAddOns);
  const filteredChooseAddOns = useSelector(getFilteredNonPurchasedChooseAddOns);
  const hasAdLite = useSelector(selectHasAdLite);
  let activeAddOn = useSelector(getActiveAddOn);
  const addOns = useSelector(
    useAddOnsForSubscription
      ? getSubscriptionEligibleAddons
      : getUserEligibleAddons
  );
  const userAddons: { [key: string]: AddOn[] } = groupBy(
    addOns,
    addOn => addOn.type
  );

  if (!userAddons) {
    return null;
  }

  if (!activeAddOn && addOns && addOns.length > 0) {
    activeAddOn = addOns[0];
  }

  const addOnGroups = keys(userAddons).sort(function (a, b) {
    return ADD_ON_PRIORITY_ORDER.indexOf(a) - ADD_ON_PRIORITY_ORDER.indexOf(b);
  });

  const closeModal = (submitted: boolean | undefined): void => {
    if (submitted) {
      changeChooseYourOwnAddOn(undefined);
    }
    toggleShowModal(false);
  };

  const getChoose = (chooseAddOnId: number): AddOn | undefined => {
    if (filteredChooseAddOns) {
      return filteredChooseAddOns.find(
        (addOn: AddOn) => addOn.id === chooseAddOnId
      );
    } else {
      return undefined;
    }
  };

  const openModal = (activeStep: string, addOn: AddOn): void => {
    dispatch(setModalStep(activeStep));
    dispatch(setActiveAddOn(addOn));
    toggleShowModal(true);
  };

  const openFromChoose = (activeStep: string, addOn: AddOn): void => {
    if (chooseYourOwnAddOn) {
      const actualAddOn =
        addOn.id === chooseYourOwnAddOn ? addOn : getChoose(chooseYourOwnAddOn);
      dispatch(setModalStep(activeStep));

      if (actualAddOn) {
        dispatch(setActiveAddOn(actualAddOn));
      }

      toggleShowModal(true);
    }
  };

  const handleDropdownChange = (addOnId: number): void => {
    changeChooseYourOwnAddOn(addOnId);
    const chosenAddOn = getChoose(addOnId);
    if (chosenAddOn) {
      dispatch(setActiveAddOn(chosenAddOn));
    }
  };

  const AddonSection = (title: string, addons: AddOn[]): JSX.Element => (
    <div
      className={styles.addonSection}
      key={`${title}-section`}
      {...setData(dataPhilter, `${title}-section`)}
    >
      <div className={styles.title}>{title}</div>
      <div className={styles.cards} {...setData(dataPhilter, `${title}-list`)}>
        {addons.map(addon => (
          <AddonCard
            addOnCardData={addon}
            addOns={addOns}
            key={`card-${addon.id}`}
            openModal={openModal}
          />
        ))}
        {title.includes(spyName) && activeAddOn && filteredChooseAddOns && (
          <AddonCard
            addOnCardData={activeAddOn}
            addOns={addOns}
            chooseYourOwnAddOn={chooseYourOwnAddOn}
            chooseYourOwnAddOnList={filteredChooseAddOns}
            chooseYourOwnSchool={true}
            handleDropdownChange={handleDropdownChange}
            key={"choose-your-own-card"}
            openModal={openFromChoose}
          />
        )}
      </div>
    </div>
  );

  return (
    <>
      {addOns && !!addOns.length && headerText && (
        <div className={styles.headerText}>{headerText}</div>
      )}
      <div className={styles.addonOptions} {...setData(dataPhilter, "")}>
        {addOnGroups.map((key: string) => {
          // Hide Ad-Lite option if user already has it
          if (key === BUNDLE || (hasAdLite && key === AD_LITE)) {
            return;
          }
          const addOnGroup = userAddons[key];
          const title =
            (addOnGroup[0].addOnGroup &&
              addOnGroup[0].addOnGroup.displayName) ||
            "";
          if (title) {
            return AddonSection(title, addOnGroup);
          }
        })}
      </div>
      {activeAddOn && (
        <AddonModal
          addOn={activeAddOn}
          addOns={addOns && addOns.concat(filteredChooseAddOns)}
          closeModal={closeModal}
          existingSpyAddOns={existingSpyAddOns}
          showModal={showModal}
        />
      )}
    </>
  );
};

export default AddonOptions;
